import {setUserLocationInCookies} from '@stubhub/location-util';
import {Controller} from '@stubhub/react-store-provider';

import {searchLocation, getLocationFromHTML5Api} from './search_location';
import {locationsSelector, locationDialogNeedOpenSelector, userLocationsSelector} from './selectors';

/**
 * Retrieves the location from the API based on the provided URL parameters and language.
 *
 * @param {Object} urlParams - The URL parameters.
 * @param {string} lang - The language.
 * @returns {Promise} A promise that resolves to the location data.
 */
function getLocationFromAPI(urlParams, lang) {
  return searchLocation(urlParams, lang);
}

/**
 * Retrieves the current location based on the user's IP address.
 * @param {string} lang - The language code for the desired language of the location information.
 * @returns {Promise} - A promise that resolves to the location information.
 */
async function getCurrentLocationFromIP(lang) {
  return await getLocationFromAPI({ipDetect: true}, lang);
}

/**
 * Retrieves the current location of the user.
 *
 * @param {string} lang - The language code for the location.
 * @returns {Promise<Object>} A promise that resolves to the user's current location.
 * @throws {string} Throws an error if the user's current location cannot be determined.
 */
function getCurrentLocation(lang) {
  return new Promise((resolve, reject) => {
    /**
     * Method to use User's IP Address to Determine the users current location.
     *
     */
    async function getLocationByIP() {
      const locations = await getCurrentLocationFromIP(lang);

      handleLocationAPIResponse(locations);
    }

    /**
     * Method To Handle Location API Response.
     * @param locations [array] The Locations Array from the Locations API's for the given Query Params.
     * @param retryLocationFromIpOnFailure [boolean] The Flag used to instruct the function to fetch location from IP if the location is not determined.
     *
     */
    function handleLocationAPIResponse(locations, retryLocationFromIpOnFailure) {
      const location = parseLocationAPIResponse(locations);

      if (location) {
        resolve(location);
      } else if (retryLocationFromIpOnFailure) {
        /**
         *If Browser Geo-Location API fails, use the User's IP Address to Determine the users current location.
         *
         */
        getLocationByIP();
      } else {
        reject('could not get user current location');
      }
    }

    /**
     * Request the Browser Geo-Location API for user's Current Location
     *
     */
    getLocationFromHTML5Api()
      .then(async (point = []) => {
        const locations = await getLocationFromAPI({point: point.join(',')}, lang);
        handleLocationAPIResponse(locations, true);

        return '';
      })
      .catch(() => {
        /**
         *If Browser Geo-Location API fails, use the User's IP Address to Determine the users current location.
         *
         */
        getLocationByIP();
      });
  });
}

/**
 * Handles the selection of a location.
 *
 * @param {Object} location - The selected location.
 * @param {Object} cookies - The cookies object.
 * @returns {Function} - The dispatch function.
 */
function onSelection(location, cookies) {
  return (dispatch, getState) => {
    if (location.key === '$0') {
      const {lang} = getState();

      return new Promise((resolve, reject) => {
        getCurrentLocation(lang)
          .then((locationObj) => {
            /**
             * Persist the User Location in the Cookie
             * Note: Cookie is used for Server Side Rendering and Component Re-hydration.
             *
             */
            const currentGeo = userLocationsSelector(getState());
            if (
              locationObj &&
              (!currentGeo ||
                currentGeo.latitude !== locationObj.latitude ||
                currentGeo.longitude !== locationObj.longitude)
            ) {
              setUserLocationInCookies(cookies, locationObj);
              dispatch({type: 'USER_LOCATION_CHANGE', location: locationObj});
            } else {
              dispatch({type: 'USER_LOCATION_CLOSE'});
            }

            return resolve(locationObj);
          })
          .catch(() => {
            reject();
          });
      });
    }

    /**
     * Persist the User Location in the Cookie
     * Note: Cookie is used for Server Side Rendering and Component Re-hydration.
     *
     */
    setUserLocationInCookies(cookies, location);

    return dispatch({type: 'USER_LOCATION_CHANGE', location});
  };
}

/**
 * Handles the change event of the geo search text input.
 *
 * @param {string} text - The new value of the geo search text input.
 * @returns {Function} A Redux thunk function.
 */
function onGeoSearchTextChange(text) {
  return (dispatch, getState) => {
    searchLocation({q: text}, getState().lang)
      .then((locations) => {
        dispatch({type: 'SEARCH_USER_LOCATION_DONE', locations});

        return '';
      })
      .catch({});
  };
}

/**
 * Pre-selects a location based on the provided text, and cookies.
 * @param {string} text - The text used for searching the location.
 * @param {object} cookies - The cookies object.
 * @returns {Function} A Redux thunk function.
 */
function preSelect(text, cookies) {
  return (dispatch, getState) => {
    return searchLocation({q: text}, getState().lang)
      .then((locations) => {
        if (locations && locations.length) {
          const [location] = locations;
          setUserLocationInCookies(cookies, location);
          const currentGeo = userLocationsSelector(getState());

          if (!currentGeo || currentGeo.latitude !== location.latitude || currentGeo.longitude !== location.longitude) {
            return dispatch({type: 'USER_LOCATION_CHANGE', location});
          }

          return dispatch({type: 'USER_LOCATION_CLOSE'});
        }

        return '';
      })
      .catch({});
  };
}

/**
 * Parses the response from the location API and returns the first location.
 *
 * @param {Array} locations - The array of locations returned from the API.
 * @returns {Object|undefined} - The first location object, or undefined if the array is empty.
 */
function parseLocationAPIResponse(locations) {
  if (locations && locations.length) {
    return locations[0];
  }
}

/**
 * Closes the location dialog.
 *
 * @returns {Function} A Redux thunk function that dispatches an action to close the location dialog.
 */
function locationDialogClose() {
  return (dispatch) => {
    return dispatch({type: 'USER_LOCATION_CLOSE'});
  };
}

/**
 * Controller for managing geo-related functionality.
 *
 * @typedef {Object} Controller
 * @property {string} namespace - The namespace for the controller.
 * @property {Function} mapStateToProps - A function that maps the state to props.
 * @property {Function} actionCreators - An object containing action creators.
 * @property {string[]} reducers - An array of reducer names.
 */
const controller = new Controller({
  namespace: 'geo',
  mapStateToProps(state) {
    return {
      currentGeo: userLocationsSelector(state),
      items: locationsSelector(state),
      locationDialogOpen: locationDialogNeedOpenSelector(state),
    };
  },
  actionCreators: {
    locationDialogClose,
    onGeoSearchTextChange,
    onSelection,
    preSelect,
  },
  reducers: ['SEARCH_USER_LOCATION_DONE'],
});

controller.addReducer('USER_LOCATION_CHANGE', (state, {location}) => {
  const userState = state.user;
  const geoState = state.geo;
  const newUserState = {...userState, location, locationDialog: 'close'};
  const newGeoState = {...geoState, locations: []};

  return {user: newUserState, geo: newGeoState};
});

controller.addReducer('USER_LOCATION_OPEN', (state) => {
  const userState = state.user;
  const newUserState = {...userState, locationDialog: 'open'};

  return {user: newUserState};
});

controller.addReducer('USER_LOCATION_CLOSE', (state) => {
  const userState = state.user;
  const newUserState = {...userState, locationDialog: 'close'};

  return {user: newUserState};
});

export default controller;
